import { useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	useServiceCore,
	type AnyService,
	type DependencyOverridesOf,
	type ToResolvedService,
	type ServiceResolverProviding,
} from '@atlassian/expansion-services/core';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { cfetchJSON } from './services/fetch';

export const useServiceEnvironmentDeps = () => {
	const { cloudId, userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useMemo(
		() => ({
			createAnalyticsEvent,
			fetchJSON: cfetchJSON,
			getCurrentCloudId: () => cloudId,
			getCurrentProduct: () => 'confluence',
			getCurrentUserId: () => userId,
		}),
		[createAnalyticsEvent, cloudId, userId],
	);
};

type EnvironmentDeps = ReturnType<typeof useServiceEnvironmentDeps>;

export const useService: ServiceResolverProviding<EnvironmentDeps> = <
	DeclaredService extends AnyService,
>(
	service: DeclaredService,
	providedDeps?: DependencyOverridesOf<DeclaredService, EnvironmentDeps>,
): ToResolvedService<DeclaredService> => {
	const environmentDeps = useServiceEnvironmentDeps();
	const deps = useMemo(
		() => ({ ...environmentDeps, ...providedDeps }),
		[environmentDeps, providedDeps],
	);

	return useServiceCore(service, deps as never);
};
